// Ported from microsites/womens-conference/src/components/Botanical.tsx verbatim.
/**
 * Hand-drawn marks, all line art, all sage.
 *
 * Rule 1 of the design system says the key art's flowers stay blurred into
 * weather. These are the other half of that rule: where the page needs a
 * flower you can actually see, it is drawn in one thin stroke, never a
 * photograph and never clipart. Everything here is decorative, so it is
 * aria-hidden and inherits `currentColor` from whatever it is placed in.
 */

/** The marker that sits on the schedule's margin rule, one per entry. */
export function Sprig({
  kind = 'leaf',
  className = '',
}: {
  kind?: 'leaf' | 'bud' | 'bloom' | 'berry' | 'seed';
  className?: string;
}) {
  const common = {
    'aria-hidden': true,
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className: `h-4 w-4 ${className}`,
  };

  if (kind === 'seed') {
    return (
      <svg {...common}>
        <circle cx="8" cy="8" r="2.1" fill="currentColor" stroke="none" />
      </svg>
    );
  }

  if (kind === 'bud') {
    return (
      <svg {...common}>
        <path d="M8 14V8" />
        <path d="M8 8c0-2.4 1.2-4.2 3-5-0.2 2.6-1.2 4.3-3 5Z" />
        <path d="M8 9.5C6.6 9.2 5.6 8.2 5 6.6c1.7 0.2 2.7 1 3 2.9Z" />
      </svg>
    );
  }

  if (kind === 'bloom') {
    return (
      <svg {...common}>
        <circle cx="8" cy="6.5" r="1.5" />
        <path d="M8 5V3.2M9.9 5.9l1.3-1.3M9.9 7.8l1.6 0.5M6.1 7.8l-1.6 0.5M6.1 5.9L4.8 4.6" />
        <path d="M8 8v6" />
        <path d="M8 11c-1.4 0-2.3-0.8-2.7-2.2 1.6-0.1 2.5 0.6 2.7 2.2Z" />
      </svg>
    );
  }

  if (kind === 'berry') {
    return (
      <svg {...common}>
        <path d="M8 14V9" />
        <circle cx="6.2" cy="6.6" r="1.7" />
        <circle cx="10" cy="7.6" r="1.4" />
        <path d="M8 9c0-1 0.4-1.8 1.1-2.4" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M8 14V4.5" />
      <path d="M8 8.6c0-2.3 1.1-3.9 3.4-4.6-0.2 2.6-1.3 4.1-3.4 4.6Z" />
      <path d="M8 11.2c-1.7-0.4-2.7-1.5-3.2-3.4 1.9 0.1 3 0.9 3.2 3.4Z" />
    </svg>
  );
}

/**
 * A rose, and it is a badge rather than decoration.
 *
 * It marks the two breakouts a woman actually sat in, in place of a chip that
 * said "You were here" — a sentence the page had to explain, where a flower
 * just reads as hers. Solid fill on the petals so it survives at twenty pixels
 * over a photograph, line work only for the stem.
 *
 * Decorative by default like everything else here; whoever uses it as a badge
 * supplies the accessible name.
 */
export function Rose({ className = '' }: { className?: string }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 16 16"
      className={`h-4 w-4 ${className}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.1"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* The cup, filled, so the bloom is a shape and not a wire drawing. */}
      <path
        d="M8 2.7c2.1 0 3.6 1.5 3.6 3.4S10 9.7 8 9.7 4.4 8 4.4 6.1 5.9 2.7 8 2.7Z"
        fill="currentColor"
        stroke="none"
      />
      {/* The spiral, drawn back out of the fill in the page's ground colour so
          it reads as petals turning rather than as a circle. */}
      <path
        d="M8.5 8.2c-1.1.2-2-.6-2-1.7 0-1 .8-1.8 1.7-1.7.8.1 1.4.7 1.3 1.5-.1.6-.6 1-1.1.9"
        className="text-wm-petal"
        stroke="currentColor"
        strokeWidth="0.9"
      />
      <path d="M8 9.7V14" />
      <path d="M8 11.7c-1.2-.2-2-.9-2.3-2.2 1.4 0 2.1.6 2.3 2.2Z" />
    </svg>
  );
}

/**
 * Lunch, drawn in the page's own hand.
 *
 * These used to be the whole story, on the reasoning that the caterer's
 * photography was the caterer's. Panera publishes theirs for public use, so the
 * pickers now lead with the actual item and these marks are the fallback: the
 * allergy option, which has no product photo, and any thumbnail whose file has
 * not been dropped in yet. See `LunchThumb`.
 */
export function FoodMark({
  kind,
  className = '',
}: {
  kind: 'sandwich' | 'salad' | 'care';
  className?: string;
}) {
  const common = {
    'aria-hidden': true,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className: `h-6 w-6 ${className}`,
  };

  if (kind === 'salad') {
    return (
      <svg {...common}>
        <path d="M3.5 12.5h17c-0.6 4.6-4.2 7.5-8.5 7.5s-7.9-2.9-8.5-7.5Z" />
        <path d="M9 12.5c-2.4-0.6-3.7-2.2-4-4.8 2.9 0.2 4.3 1.8 4 4.8Z" />
        <path d="M13.4 12.5c0.4-2.9 2.2-4.5 5.3-4.8-0.6 3-2.4 4.6-5.3 4.8Z" />
        <circle cx="11.6" cy="9.4" r="1.9" />
      </svg>
    );
  }

  if (kind === 'care') {
    return (
      <svg {...common}>
        <path d="M12 19.5c-3.6-2.4-6.5-4.9-6.5-8.3A3.6 3.6 0 0 1 12 8.9a3.6 3.6 0 0 1 6.5 2.3c0 3.4-2.9 5.9-6.5 8.3Z" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M4.5 18.5 12 5.5l7.5 13Z" />
      <path d="M7.4 15.4c1.6-1.1 3-1.1 4.6 0 1.6 1.1 3 1.1 4.6 0" />
    </svg>
  );
}

/** The pen stroke under a day name: not a border, a gesture. */
export function Swash({ className = '' }: { className?: string }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 200 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      className={`h-3 w-40 ${className}`}
      preserveAspectRatio="none"
    >
      <path d="M2 8.5c26-4.6 52-6 78-4.4 24 1.5 47 4.9 71 3.1" />
      <path d="M162 6.6c3.6-2.6 7.2-3.4 10.8-2.4-1.4 3.2-5 4-10.8 2.4Z" strokeWidth="1.1" />
      <path d="M172.8 4.2c4.4-0.5 8.4 0.7 12 3.6" strokeWidth="1.1" />
    </svg>
  );
}

/**
 * A tall climbing stem for the page margins. Only appears where there is
 * real room for it (xl and up); on a phone the paper stays plain.
 */
export function Vine({ className = '', flip = false }: { className?: string; flip?: boolean }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 60 380"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.1"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`${flip ? 'scale-x-[-1]' : ''}${className}`}
    >
      {/* The stem ends at the flower, not past it: the bloom is the tip. */}
      <path d="M30 378C18 330 40 300 30 250 22 208 44 178 30 128 24 98 34 68 30 39" />
      <path d="M30 300c-13-2-20-10-22-24 15 1 22 8 22 24Z" />
      <path d="M31 246c12-3 18-12 19-26-14 2-20 9-19 26Z" />
      <path d="M30 190c-14-1-21-9-24-23 16 0 23 7 24 23Z" />
      <path d="M31 132c13-2 19-11 20-25-15 1-21 8-20 25Z" />
      <path d="M29 82c-13-2-19-10-21-24 15 1 21 8 21 24Z" />
      {/* A rose, the key art's flower, rather than the daisy that used to sit
          here. Three things make it read as one: six outer petals whose
          notches do NOT line up with the four inner ones, so the layers look
          wrapped instead of concentric; a coil set off to one side, since a
          coil centred in a circle reads as a shell; and the whole head drawn
          larger than the daisy was, because at 26px the layers collapsed into
          a blob and only the daisy's radiating lines survived that size. */}
      <path d="M29.8 8.4Q35.6 8.8 37.2 13.8Q42.8 12.9 44.2 18.6Q47.1 23.7 42.4 28.1Q42.9 34.1 36.8 35.6Q33.8 40.6 28.3 39.1Q22.8 40.5 20.3 35.4Q14.8 33.9 15.4 27.9Q11.4 23.4 15 18.4Q16.6 12.8 22.1 13.9Q24.4 8.9 29.8 8.4Z" />
      <path d="M30.2 14.8Q34.9 15.4 36.2 20.1Q39.2 24.2 35.4 28.1Q31.8 31.6 27.8 29.9Q23.3 28.8 22.4 24.3Q21.9 18.8 26.1 16.3Q28.1 15 30.2 14.8Z" />
      <path d="M27.4 25.1c-.9-3.3 1.3-5.8 4.2-5.2 2.7.6 3.7 3.7 1.6 5.5-1.7 1.5-4.3 1-4.6-1.1" />
      <path d="M18.6 30.6c1 2.6 2.9 4.5 5.6 5.6" strokeWidth="0.9" />
    </svg>
  );
}

/**
 * The prayer card's mark: one long-stemmed rose, laid in where a badge disc
 * used to sit. The head is the Vine's rose, so the page has one rose drawn
 * one way; the stem bows and carries two leaves so it reads as cut, not
 * clip art. Line work only, in whatever colour it is set in.
 */
export function LongRose({ className = '' }: { className?: string }) {
  return (
    <svg
      aria-hidden
      viewBox="10 4 40 104"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M29.8 8.4Q35.6 8.8 37.2 13.8Q42.8 12.9 44.2 18.6Q47.1 23.7 42.4 28.1Q42.9 34.1 36.8 35.6Q33.8 40.6 28.3 39.1Q22.8 40.5 20.3 35.4Q14.8 33.9 15.4 27.9Q11.4 23.4 15 18.4Q16.6 12.8 22.1 13.9Q24.4 8.9 29.8 8.4Z" />
      <path d="M30.2 14.8Q34.9 15.4 36.2 20.1Q39.2 24.2 35.4 28.1Q31.8 31.6 27.8 29.9Q23.3 28.8 22.4 24.3Q21.9 18.8 26.1 16.3Q28.1 15 30.2 14.8Z" />
      <path d="M27.4 25.1c-.9-3.3 1.3-5.8 4.2-5.2 2.7.6 3.7 3.7 1.6 5.5-1.7 1.5-4.3 1-4.6-1.1" />
      {/* The stem, bowing once, and its two leaves on opposite sides. */}
      <path d="M30 39.5c-2.6 18 3.4 34-1.4 52-1.2 4.6-2.8 9-4.6 13" />
      <path d="M30.6 62c7.4-1.6 12-6.4 13.4-14-8 1.4-12.4 6-13.4 14Z" />
      <path d="M30.6 62c2.6-4 6.8-7.4 12-9.6" strokeWidth="0.9" />
      <path d="M29.4 80c-7.6-0.8-12.6-5.2-14.6-12.6 8.2 0.6 13 5 14.6 12.6Z" />
      <path d="M29.4 80c-3-3.6-7.4-6.6-12.8-8.4" strokeWidth="0.9" />
    </svg>
  );
}

/**
 * The question card's mark: a note in an envelope with a sprig tucked behind
 * the flap. Stands in for the host's face when MP has no photo of her; a
 * monogram in a disc said nothing a person could not read off the heading.
 */
export function Letter({ className = '' }: { className?: string }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 60 50"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* The sprig first, so the envelope's edge reads as in front of it. */}
      <path d="M38 20c3-6 7.4-11 13.6-15" />
      <path d="M45.6 9.6c-0.4-4 1.4-7 5-8.6 0.4 4-1.4 7-5 8.6Z" />
      <path d="M43 13.4c-3.8-1.2-5.8-3.8-6-7.6 3.8 1 5.8 3.6 6 7.6Z" />
      <circle cx="53.4" cy="5.2" r="1.6" />
      <path d="M8.5 18h35A2.5 2.5 0 0 1 46 20.5v22A2.5 2.5 0 0 1 43.5 45h-35A2.5 2.5 0 0 1 6 42.5v-22A2.5 2.5 0 0 1 8.5 18Z" />
      <path d="M7 19.6l19 13.6 19-13.6" />
      <path d="M7 43.6l13.6-11.2M45 43.6L31.4 32.4" strokeWidth="0.9" />
    </svg>
  );
}
